// 戦闘アイテムの持ち出し（育成側）。出撃前に2種類を選び、毎戦その数だけ支給される（在庫は持たない）。
// 種類は万屋で解放（主Lv が ITEM_UNLOCK に届くと並ぶ。回復薬は最初から）。スキル・お守り・お供の「回復薬+1（potionCount）」は、回復薬を持っていくときだけその数に足す。
import type { CharId, ItemId } from '../types';
import { ITEM_IDS } from '../types';
import type { CharKit, OpResult, Profile } from './profile';

/** 持ち出せる種類の数 */
export const ITEM_SLOTS = 2;
/** 種類ごとに、万屋に並ぶ主Lv */
export const ITEM_UNLOCK: Record<ItemId, number> = { heal: 1, power: 19, guard: 46, swift: 82, purify: 127, spirit: 172 };
/** 1戦に支給される数（回復薬はスキルなどの potionCount がさらに足される） */
export const ITEM_BASE_COUNT: Record<ItemId, number> = { heal: 2, power: 1, guard: 1, swift: 1, purify: 1, spirit: 1 };

/** 持ち出せる道具（回復薬と、万屋で解放したもの） */
export const unlockedItems = (p: Profile): ItemId[] => ITEM_IDS.filter((id) => id === 'heal' || (p.ownedItems ?? []).includes(id));

/** そのキャラの持ち出し（解放されていないもの・重複は落とす。空なら回復薬）。charId を省くと出撃キャラ */
export function battleItemsOf(p: Profile, charId: CharId = p.oshi): ItemId[] {
  const open = unlockedItems(p);
  const out: ItemId[] = [];
  for (const id of p.kits?.[charId]?.items ?? p.battleItems ?? []) if (open.includes(id) && !out.includes(id) && out.length < ITEM_SLOTS) out.push(id);
  return out.length ? out : ['heal'];
}

/** そのキャラが持ち出すアイテムを決める（1〜2種類・持っているものだけ）。charId を省くと出撃キャラ */
export function setBattleItems(p: Profile, ids: unknown, charId: CharId = p.oshi): OpResult<{ battleItems: ItemId[] }> {
  if (!Array.isArray(ids) || ids.length < 1 || ids.length > ITEM_SLOTS) return { ok: false, error: 'count' };
  const list: ItemId[] = [];
  for (const id of ids) {
    if (typeof id !== 'string' || !ITEM_IDS.includes(id as ItemId)) return { ok: false, error: 'unknown_item' };
    if (list.includes(id as ItemId)) return { ok: false, error: 'duplicate' };
    if (!unlockedItems(p).includes(id as ItemId)) return { ok: false, error: 'locked' };
    list.push(id as ItemId);
  }
  setKit(p, charId, { items: list });
  return { ok: true, battleItems: list };
}

/** 支給される数（種類ごと）。healBonus は potionCount（回復薬+n） */
export function itemCounts(p: Profile, healBonus: number, charId: CharId = p.oshi): { id: ItemId; count: number }[] {
  const out = battleItemsOf(p, charId).map((id) => ({ id, count: ITEM_BASE_COUNT[id] }));
  // 回復薬の追加（薬袋など）は、回復薬を持っていくときだけ数に足す（持ち物は2つまで。3つ目として足さない）
  const bonus = Math.max(0, Math.round(healBonus));
  const h = out.find((x) => x.id === 'heal');
  if (bonus > 0 && h) h.count += bonus;
  return out;
}

/** キャラの持ち物を書き換える（無ければ今の設定から作る） */
export function setKit(p: Profile, charId: CharId, patch: Partial<CharKit>) {
  const cur = p.kits?.[charId] ?? { companion: p.companion ?? null, items: [...(p.battleItems ?? ['heal'])] };
  const next = { ...cur, ...patch };
  p.kits = { ...(p.kits ?? {}), [charId]: next };
  // 旧の共通の欄は「出撃キャラの持ち物」の写しとして残す（古い画面・データとの互換）
  if (charId === p.oshi) {
    p.companion = next.companion;
    p.battleItems = [...next.items];
  }
}
